/**
 * Aspect Admin — Clients / accounts / organizations.
 */

import { icon } from "./icons.js";
import { renderShell, pageHead, statusBadge, openDrawer, openModal, toast, initials, closeOverlay, dropdownHtml, bindDropdowns } from "./shell.js";
import { escapeHtml } from "./auth_shell.js";
import { getSession } from "../auth_store.js";
import { navigate } from "../router.js";
import { t } from "../i18n.js";
import { CLIENTS, SPACES } from "../mock_data.js";

let state = { query: "", sort: "name" };

function healthBadge(h) {
  if (h >= 80) return `<span class="ap-badge ap-badge--ok">${h}</span>`;
  if (h >= 60) return `<span class="ap-badge ap-badge--cyan">${h}</span>`;
  if (h >= 45) return `<span class="ap-badge ap-badge--warn">${h}</span>`;
  return `<span class="ap-badge ap-badge--err">${h}</span>`;
}

function clientRow(c) {
  return `
    <tr data-row data-client="${c.id}">
      <td>
        <div class="ap-entity">
          <div class="ap-avatar">${initials(c.name)}</div>
          <div>
            <span class="ap-cell-main">${escapeHtml(c.name)}</span>
            <span class="ap-cell-sub">${escapeHtml(c.contact)} · ${escapeHtml(c.email)}</span>
          </div>
        </div>
      </td>
      <td>${statusBadge(c.status)}</td>
      <td class="ap-cell-num">${c.spaces}</td>
      <td class="ap-cell-num">${c.seats}</td>
      <td class="ap-cell-num">${c.mrr}</td>
      <td class="ap-cell-num">${healthBadge(c.health)}</td>
      <td>${t(c.since)}</td>
      <td class="ap-cell-actions"><button type="button" class="ap-btn ap-btn--ghost ap-btn--sm">${t("View")} ${icon("chevronRight", 12)}</button></td>
    </tr>
  `;
}

function clientDrawer(root, c) {
  const clientSpaces = SPACES.filter((s) => s.client === c.name).slice(0, 4);
  openDrawer(root, {
    title: c.name,
    subtitle: t("Client since {s} · {n} seats", { s: t(c.since), n: c.seats }),
    bodyHtml: `
      <div style="display:flex;align-items:center;gap:0.9rem;">
        <div class="ap-avatar" style="width:3rem;height:3rem;font-size:1rem;border-radius:14px;">${initials(c.name)}</div>
        <div style="flex:1;">
          <div style="display:flex;gap:0.5rem;align-items:center;">${statusBadge(c.status)} ${healthBadge(c.health)}<span class="ap-note">${t("health")}</span></div>
          <p class="ap-note" style="margin:0.3rem 0 0;">${escapeHtml(c.contact)} · <a class="portal-link" href="mailto:${escapeHtml(c.email)}">${escapeHtml(c.email)}</a></p>
        </div>
      </div>
      ${c.status === "churn-risk" ? `
      <div class="ap-insight" style="border-color:rgba(196,138,32,0.35);">
        <div class="ap-insight__head">${icon("sparkles", 15)}<span class="ap-insight__tag" style="color:var(--ap-warn);">${t("Churn signal")}</span></div>
        <p class="ap-insight__body">${t("No tours published in 34 days and seat activity fell 71%. Similar accounts churned within 6 weeks. Suggested play: schedule a success call and offer an AI staging workshop.")}</p>
        <div class="ap-insight__foot"><span></span><button type="button" class="ap-btn ap-btn--soft ap-btn--sm" data-drawer-play>${t("Run retention play")}</button></div>
      </div>` : ""}
      <div class="ap-drawer__section">
        <h4>${t("Account")}</h4>
        <dl class="ap-def">
          <dt>MRR</dt><dd>${c.mrr}</dd>
          <dt>${t("Active spaces")}</dt><dd>${c.spaces}</dd>
          <dt>${t("Seats used")}</dt><dd>${c.seats}</dd>
          <dt>${t("Primary contact")}</dt><dd>${escapeHtml(c.contact)}</dd>
          <dt>${t("Region")}</dt><dd>EU (Tallinn)</dd>
        </dl>
      </div>
      ${clientSpaces.length ? `
      <div class="ap-drawer__section">
        <h4>${t("Spaces")}</h4>
        <div class="ap-stack" style="gap:0.45rem;">
          ${clientSpaces
            .map(
              (s) => `
            <div class="ap-row" style="justify-content:space-between;padding:0.5rem 0.65rem;border:1px solid var(--ap-hairline);border-radius:10px;background:var(--ap-surface-low);">
              <span style="font-size:0.82rem;font-weight:600;color:var(--ap-ink);">${escapeHtml(s.name)}</span>
              ${statusBadge(s.status)}
            </div>`
            )
            .join("")}
        </div>
      </div>` : ""}
      <div class="ap-drawer__section">
        <h4>${t("Timeline")}</h4>
        <div class="ap-timeline">
          <div class="ap-timeline__item">${t("<strong>Invoice paid</strong> — {m}", { m: c.mrr === "—" ? t("trial period") : t("{m} monthly", { m: c.mrr }) })}<span class="ap-timeline__time">${t("Jul 1")}</span></div>
          <div class="ap-timeline__item ap-timeline__item--mute">${t("<strong>3 new spaces</strong> created by {c}", { c: escapeHtml(c.contact) })}<span class="ap-timeline__time">${t("Jun 24")}</span></div>
          <div class="ap-timeline__item ap-timeline__item--mute">${t("<strong>Account created</strong> · {s}", { s: t(c.since) })}<span class="ap-timeline__time">${t(c.since)}</span></div>
        </div>
      </div>
      <div class="ap-drawer__section">
        <h4>${t("Internal notes")}</h4>
        <textarea class="ap-textarea" placeholder="${t("Add a note for your team…")}"></textarea>
      </div>
    `,
    footHtml: `
      <button type="button" class="ap-btn" data-drawer-email>${icon("mail", 15)} ${t("Email contact")}</button>
      <button type="button" class="ap-btn ap-btn--primary" data-drawer-save>${t("Save note")}</button>
    `,
  });
  const slot = root.querySelector("#ap-overlay-slot");
  slot?.querySelector("[data-drawer-play]")?.addEventListener("click", () => toast(root, t("Retention play started — success call scheduled"), "ok"));
  slot?.querySelector("[data-drawer-email]")?.addEventListener("click", () => toast(root, t("Draft email opened for {c}", { c: c.contact }), "info"));
  slot?.querySelector("[data-drawer-save]")?.addEventListener("click", () => toast(root, t("Note saved"), "ok"));
}

function newClientModal(root) {
  const panel = openModal(root, {
    title: t("Add client organization"),
    bodyHtml: `
      <div class="ap-field"><label class="ap-field__label">${t("Organization name")}</label><input class="ap-input" placeholder="e.g. Meridian Group" /></div>
      <div class="ap-grid ap-grid--2" style="gap:0.8rem;">
        <div class="ap-field"><label class="ap-field__label">${t("Primary contact")}</label><input class="ap-input" placeholder="${t("Full name")}" /></div>
        <div class="ap-field"><label class="ap-field__label">${t("Email")}</label><input class="ap-input" type="email" placeholder="name@company.com" /></div>
      </div>
      <div class="ap-field">
        <label class="ap-field__label">${t("Plan")}</label>
        ${dropdownHtml({
          value: t("Trial (14 days)"),
          options: [t("Trial (14 days)"), "Starter", "Growth", "Scale"],
        })}
      </div>
      <label class="ap-row" style="gap:0.6rem;cursor:pointer;">
        <span class="ap-switch"><input type="checkbox" checked /><span class="ap-switch__track"></span></span>
        <span style="font-size:0.82rem;color:var(--ap-slate);">${t("Send invitation email with portal access")}</span>
      </label>
    `,
    footHtml: `
      <button type="button" class="ap-btn ap-btn--ghost" data-mclose>${t("Cancel")}</button>
      <button type="button" class="ap-btn ap-btn--primary" data-mcreate>${icon("plus", 15)} ${t("Create client")}</button>
    `,
  });
  panel?.querySelector("[data-mclose]")?.addEventListener("click", () => closeOverlay(root));
  panel?.querySelector("[data-mcreate]")?.addEventListener("click", () => {
    closeOverlay(root);
    toast(root, t("Client created and invitation sent"), "ok");
  });
  bindDropdowns(panel);
}

export function renderClients(root) {
  const session = getSession();
  if (!session) { navigate("/login", true); return; }

  const q = state.query.trim().toLowerCase();
  let list = CLIENTS.filter((c) => !q || `${c.name} ${c.contact} ${c.email}`.toLowerCase().includes(q));
  if (state.sort === "mrr") list = [...list].sort((a, b) => (b.mrr === "—" ? 0 : parseInt(b.mrr.slice(1))) - (a.mrr === "—" ? 0 : parseInt(a.mrr.slice(1))));
  if (state.sort === "health") list = [...list].sort((a, b) => b.health - a.health);

  const totals = {
    active: CLIENTS.filter((c) => c.status === "active").length,
    trial: CLIENTS.filter((c) => c.status === "trial").length,
    risk: CLIENTS.filter((c) => c.status === "churn-risk").length,
  };

  const content = `
    ${pageHead(t("Clients"), t("{n} organizations · {a} active · {t} on trial · {r} at risk", { n: CLIENTS.length, a: totals.active, t: totals.trial, r: totals.risk }), `
      <button type="button" class="ap-btn" id="cl-export">${icon("download", 15)} ${t("Export CSV")}</button>
      <button type="button" class="ap-btn ap-btn--primary" id="cl-new">${icon("plus", 15)} ${t("Add client")}</button>
    `)}

    <div class="ap-toolbar">
      <div class="ap-searchfield" style="width:260px;">
        ${icon("search", 15)}
        <input class="ap-input" id="cl-search" type="text" placeholder="${t("Search clients…")}" value="${escapeHtml(state.query)}" />
      </div>
      <div class="ap-toolbar__spacer"></div>
      <span class="ap-note">${t("Sort by")}</span>
      <div class="ap-seg">
        ${[["name", t("Name")], ["mrr", "MRR"], ["health", t("Health")]]
          .map(([id, label]) => `<button type="button" class="ap-seg__btn ${state.sort === id ? "ap-seg__btn--on" : ""}" data-sort="${id}">${label}</button>`)
          .join("")}
      </div>
    </div>

    <div class="ap-panel">
      <div class="ap-tablewrap ap-scroll">
        <table class="ap-table">
          <thead><tr><th>${t("Organization")}</th><th>${t("Status")}</th><th class="ap-cell-num">${t("Spaces")}</th><th class="ap-cell-num">${t("Seats")}</th><th class="ap-cell-num">MRR</th><th class="ap-cell-num">${t("Health")}</th><th>${t("Since")}</th><th></th></tr></thead>
          <tbody>${list.map(clientRow).join("")}</tbody>
        </table>
      </div>
      <div class="ap-panel__foot">
        <span>${t("{a} of {b} organizations", { a: list.length, b: CLIENTS.length })}</span>
        <span>${t("Health scores refresh nightly")}</span>
      </div>
    </div>
  `;

  const el = renderShell(root, "/clients", content);
  if (!el) return;

  el.querySelector("#cl-new")?.addEventListener("click", () => newClientModal(root));
  el.querySelector("#cl-export")?.addEventListener("click", () => toast(root, t("CSV export downloading…"), "ok"));

  el.querySelectorAll("[data-sort]").forEach((b) =>
    b.addEventListener("click", () => {
      state = { ...state, sort: b.dataset.sort };
      renderClients(root);
    })
  );

  const search = el.querySelector("#cl-search");
  search?.addEventListener("input", () => {
    state = { ...state, query: search.value };
    clearTimeout(search._t);
    search._t = setTimeout(() => {
      const pos = search.selectionStart;
      renderClients(root);
      const s2 = root.querySelector("#cl-search");
      s2?.focus();
      s2?.setSelectionRange(pos, pos);
    }, 240);
  });

  el.querySelector("tbody")?.addEventListener("click", (e) => {
    const row = e.target.closest("[data-client]");
    if (!row) return;
    const c = CLIENTS.find((x) => x.id === row.dataset.client);
    if (c) clientDrawer(root, c);
  });
}
